import { Loader2, RotateCcw, RotateCw } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

import { DoclistTreeItemSectionsItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import { trackEvent } from "~/utils/analytics";

interface RotateSectionPagesDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  section: DoclistTreeItemSectionsItem;
  onRotate: (degrees: number) => Promise<void>;
  isRotating: boolean;
}

export const RotateSectionPagesDialog: FC<RotateSectionPagesDialogProps> = ({
  open,
  setOpen,
  section,
  onRotate,
  isRotating,
}) => {
  const [rotation, setRotation] = useState(0);

  const normalizedRotation = ((rotation % 360) + 360) % 360;

  useEffect(() => {
    if (open) {
      setRotation(0);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isRotating) return;
    if (e.key === "ArrowRight" || e.key === " ") {
      e.preventDefault();
      setRotation((r) => r + 90);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setRotation((r) => r - 90);
    }
  };

  const handleRotate = async () => {
    trackEvent({
      eventName: "app/doc_list/rotate_section_dialog/rotate.button/click",
    });
    try {
      await onRotate(normalizedRotation);
      setOpen(false);
    } catch {
      // Error is already handled in onRotate (toast shown)
      // Keep dialog open so user can retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !isRotating && setOpen(open)}>
      <DialogContent className="w-full max-w-[400px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="docList.rotateSectionDialog.title"
              defaultMessage="Rotate All Pages"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="docList.rotateSectionDialog.description"
              defaultMessage="Rotate all pages in {name}"
              values={{ name: <strong>{section.name}</strong> }}
            />
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-4 py-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setRotation((r) => r - 90)}
            disabled={isRotating}
            aria-label="Rotate counter-clockwise"
          >
            <RotateCcw className="w-5 h-5" />
          </Button>

          <button
            onClick={() => !isRotating && setRotation((r) => r + 90)}
            onKeyDown={handleKeyDown}
            className="group relative focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg disabled:opacity-50"
            aria-label={`Page rotated ${normalizedRotation} degrees. Click or press arrow keys to rotate.`}
            disabled={isRotating}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs font-medium px-2.5 py-0.5 rounded-full z-10">
              {normalizedRotation}°
            </div>

            <div
              className="relative w-20 h-[114px] bg-white border border-gray-300 rounded-sm shadow-sm transition-all duration-500 cursor-pointer overflow-hidden"
              style={{ transform: `rotate(${rotation}deg)` }}
            >
              <div className="absolute top-0 right-0 w-3 h-3">
                <div className="absolute top-0 right-0 w-0 h-0 border-l-[12px] border-l-transparent border-t-[12px] border-t-gray-200" />
              </div>

              <div className="p-2.5 pt-4 space-y-1.5">
                <div className="w-full h-0.5 bg-gray-300 rounded" />
                <div className="w-4/5 h-0.5 bg-gray-200 rounded" />
                <div className="w-full h-0.5 bg-gray-200 rounded" />
                <div className="w-3/5 h-0.5 bg-gray-200 rounded" />
                <div className="w-full h-0.5 bg-gray-200 rounded" />
                <div className="w-4/5 h-0.5 bg-gray-200 rounded" />
                <div className="w-2/3 h-0.5 bg-gray-200 rounded" />
                <div className="w-full h-0.5 bg-gray-200 rounded" />
                <div className="w-1/2 h-0.5 bg-gray-200 rounded" />
              </div>
            </div>

            {!isRotating && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <RotateCw className="w-6 h-6 text-blue-600 drop-shadow-md" />
              </div>
            )}
          </button>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setRotation((r) => r + 90)}
            disabled={isRotating}
            aria-label="Rotate clockwise"
          >
            <RotateCw className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              disabled={isRotating}
              onClick={() => {
                trackEvent({
                  eventName:
                    "app/doc_list/rotate_section_dialog/cancel.button/click",
                });
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
          </DialogClose>
          <Button
            onClick={handleRotate}
            disabled={normalizedRotation === 0 || isRotating}
            className="min-w-[100px]"
          >
            {isRotating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                <FormattedMessage
                  id="docList.rotateSectionDialog.rotating"
                  defaultMessage="Rotating..."
                />
              </>
            ) : (
              <FormattedMessage
                id="docList.rotateSectionDialog.rotate"
                defaultMessage="Rotate"
              />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
